import React from "react";

/**
 * HearClear-powered CochlearImplantSurgery.in brand mark.
 * Custom SVG — stylized cochlear spiral + sound waves on a soft blue/teal gradient.
 *
 * Variants:
 *   variant="mark"      — icon only (used for favicon-like spots / mobile)
 *   variant="full"      — icon + wordmark "CochlearImplantSurgery.in" + "Powered by HearClear"
 *   variant="wordmark"  — text only (admin headers in tight rows)
 *
 * Tone prop adjusts text colour for light vs dark surfaces.
 */
export default function Logo({
  variant = "full",
  tone = "dark",          // "dark" for light bg, "light" for dark bg
  size = 44,
  className = "",
  testId,
}) {
  const textColor = tone === "light" ? "#FFFFFF" : "#0F172A";
  const subColor = tone === "light" ? "#94A3B8" : "#475569";
  const dotColor = "#0F766E";

  const Mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0"
    >
      <defs>
        <linearGradient id={`hc-grad-${size}`} x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#0369A1" />
          <stop offset="1" stopColor="#0F766E" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="64" height="64" rx="16" fill={`url(#hc-grad-${size})`} />
      {/* Sound waves entering */}
      <path d="M11 32 Q14 26 14 32 Q14 38 11 32 Z" fill="#ffffff" opacity="0.55" />
      <path d="M16 32 Q21 22 21 32 Q21 42 16 32 Z" fill="#ffffff" opacity="0.75" />
      {/* Cochlear spiral (concentric arc inward) */}
      <path
        d="M44 24 a10 10 0 1 0 0 16 a7 7 0 1 1 0 -12 a4 4 0 1 0 0 8"
        stroke="#ffffff"
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="44" cy="32" r="2" fill="#ffffff" />
    </svg>
  );

  if (variant === "mark") {
    return <span data-testid={testId} className={className}>{Mark}</span>;
  }

  if (variant === "wordmark") {
    return (
      <span data-testid={testId} className={`leading-tight ${className}`}>
        <span className="font-serif font-semibold text-[1.05rem]" style={{ color: textColor }}>
          CochlearImplantSurgery
          <span style={{ color: dotColor }}>.in</span>
        </span>
      </span>
    );
  }

  // full
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-2.5 ${className}`}>
      {Mark}
      <span className="leading-tight">
        <span className="block font-serif font-semibold text-[1.05rem]" style={{ color: textColor }}>
          CochlearImplantSurgery<span style={{ color: dotColor }}>.in</span>
        </span>
        <span className="block text-[10.5px] uppercase tracking-[0.12em] font-bold" style={{ color: subColor }}>
          Powered by HearClear
        </span>
      </span>
    </span>
  );
}
